import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { includes } from 'lodash-es'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { TtIconButton } from '@23/react'
import { intl } from 'lang/intl'
import { useMount } from 'react-use'
import {
  VideoSelectorModalComponent,
  SelectionItemComponent,
  Button as ButtonComponent,
  LoaderWrapperComponent,
  LoaderComponent,
} from '../../../../sharedLegacy/components'
import {
  playbackSelectorClosed,
  openPlaybackSelector,
} from '../../actions.playback'
import { getStagedPlaybackVideos } from '../../selectors.playback'

import './PlaybackSelectorComponent.scss'
import { usePlaybackVideoId } from '../../../../firebase/hooks/useVideoPlayback'
import { useIsVideoShowing } from '../../../../firebase/hooks/useFloor'

const getPhotoId = (video) => video.photo_id || video.key

const PlaybackSelectorComponent = (props) => {
  const {
    playingVideoIsPaused,
    selectedPlaybackPhotoId,
    pendingPlaybackPhotoId,
    playbackState,
    togglePlayback,
    setPlaybackPlayIntent,
    loadPlaybackVideos,
    liveId,
    stagedPlaybackVideos,
    isFetchingPlaybackVideos,
    unstagePlaybackVideo,
    stagePlaybackVideos,
    webinarColors,
    isPlaybackSelectorOpen,
    playbackSelectorClosed: playBackSelectorClosedAction,
    openPlaybackSelector: openPlaybackSelectorAction,
  } = props

  useMount(() => {
    loadPlaybackVideos(liveId)
  })

  const playbackVideoId = usePlaybackVideoId()

  const isVideoShowing = useIsVideoShowing()

  const onVideoClicked = (e, video) => {
    if (
      e.target.classList.contains('buttons') ||
      e.target.parentElement.classList.contains('buttons') ||
      e.target.parentElement.classList.contains('play-icon')
    ) {
      return
    }

    setPlaybackPlayIntent(video)
  }

  const areButtonsVisible = (video) => {
    const isPendingOrSelected = includes(
      [selectedPlaybackPhotoId, pendingPlaybackPhotoId],
      getPhotoId(video)
    )

    return isPendingOrSelected && !playingVideoIsPaused
  }

  const renderPlaybackButton = (video) => {
    const photoId = getPhotoId(video)
    const videoName = video.title || video.name

    const playAriaLabel = intl.formatMessage(
      { defaultMessage: 'Play video with the following title: {videoName}' },
      { videoName }
    )

    const pauseAriaLabel = intl.formatMessage(
      { defaultMessage: 'Pause video with the following title: {videoName}' },
      { videoName }
    )

    const conditionalAriaLabel = playingVideoIsPaused
      ? playAriaLabel
      : pauseAriaLabel

    if (photoId === pendingPlaybackPhotoId) {
      return <LoaderComponent size="small" />
    }

    if (
      photoId === playbackVideoId &&
      isVideoShowing &&
      playbackState !== 'ended'
    ) {
      return (
        <TtIconButton
          className="play-icon"
          name={playingVideoIsPaused ? 'play' : 'pause'}
          library="23"
          ariaLabel={conditionalAriaLabel}
          onClick={togglePlayback}
        />
      )
    }

    return (
      <TtIconButton
        className="play-icon"
        name="play"
        library="23"
        ariaLabel={playAriaLabel}
        onClick={() => setPlaybackPlayIntent(video)}
      />
    )
  }

  return (
    <div className="playback-selector-component">
      {stagedPlaybackVideos.length > 0 && (
        <>
          <VideoSelectorModalComponent
            onChange={stagePlaybackVideos}
            value={stagedPlaybackVideos}
            selectorProps={{
              searchIn: ['photos'],
              theme: 'dark',
              selectionLimit: 50,
            }}
            isModalOpen={isPlaybackSelectorOpen}
            onCloseModal={playBackSelectorClosedAction}
          />
          <div className="button-wrapper">
            <ButtonComponent
              style={{ color: webinarColors.accent.contrast }}
              onClick={openPlaybackSelectorAction}
              className="select-videos-button"
              round
            >
              <FormattedMessage defaultMessage="Select videos" />
            </ButtonComponent>
          </div>

          <div className="selected-videos">
            <LoaderWrapperComponent loading={isFetchingPlaybackVideos}>
              {stagedPlaybackVideos?.map((video) => (
                <SelectionItemComponent
                  item={video}
                  className={classnames({
                    'buttons-visible': areButtonsVisible(video),
                  })}
                  key={getPhotoId(video)}
                  toggleItem={unstagePlaybackVideo}
                  theme="webinar"
                  renderExtraButtons={() => renderPlaybackButton(video)}
                  onClick={(e) => onVideoClicked(e, video)}
                />
              ))}
            </LoaderWrapperComponent>
          </div>
        </>
      )}

      <div className="playback-selector-deprecation-warning">
        {stagedPlaybackVideos.length > 0 ? (
          <p>
            We will soon be removing this video playback panel in favor of
            improved video functionalities in the WebinarTray.
          </p>
        ) : (
          <p>
            We have improved video playback and moved it to the WebinarTray.
          </p>
        )}
        <p>
          To play a video, simply add a video to the WebinarTray and click it.
        </p>
      </div>
    </div>
  )
}

PlaybackSelectorComponent.propTypes = {
  stagePlaybackVideos: PropTypes.func,
  stagedPlaybackVideos: PropTypes.arrayOf(PropTypes.object),
  unstagePlaybackVideo: PropTypes.func,
  isPlaybackSelectorOpen: PropTypes.bool,
  playbackSelectorClosed: PropTypes.func,
  openPlaybackSelector: PropTypes.func,
  loadPlaybackVideos: PropTypes.func,
  setPlaybackPlayIntent: PropTypes.func,
  togglePlayback: PropTypes.func,
  liveId: PropTypes.number,
  isFetchingPlaybackVideos: PropTypes.bool,
  selectedPlaybackPhotoId: PropTypes.string,
  pendingPlaybackPhotoId: PropTypes.string,
  playingVideoIsPaused: PropTypes.bool,
  playbackState: PropTypes.string,
  webinarColors: PropTypes.object,
}

const mapStateToProps = (state) => ({
  stagedPlaybackVideos: getStagedPlaybackVideos(state),
  isPlaybackSelectorOpen: state.webinarPlayback.isPlaybackSelectorOpen,
  isFetchingPlaybackVideos: state.webinarPlayback.isFetchingPlaybackVideos,
  playingVideoIsPaused: state.webinarPlayback.playingVideoIsPaused,
  selectedPlaybackPhotoId: state.webinarPlayback.selectedPlaybackPhotoId,
  pendingPlaybackPhotoId: state.webinarPlayback.pendingPlaybackPhotoId,
  playbackState: state.webinarPlayback.playbackState,
})

export default connect(mapStateToProps, {
  playbackSelectorClosed,
  openPlaybackSelector,
})(PlaybackSelectorComponent)
